import { Bus, ArrowLeft, CheckCircle } from "lucide-react";
import ContactForm from "@/components/ContactForm";
import ContactInfo from "@/components/ContactInfo";
import serviceBus from "@/assets/service-bus.jpg";
import { useLanguage } from "@/i18n/LanguageContext";
import SeoMetadata from "@/components/SeoMetadata";

const BusControllers = () => {
  const { t, language } = useLanguage();

  const seoTitle = `${t("bus.pageTitle")} | Gendera Service`;
  const seoDescription = t("service.bus.desc");

  return (
    <div className="min-h-screen bg-background">
      <SeoMetadata title={seoTitle} description={seoDescription} />
      <main className="pt-20">
        <section className="relative h-[400px] overflow-hidden">
          <img
            src={serviceBus}
            alt={t("bus.pageTitle")}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-primary/80" />
          <div className="absolute inset-0 flex items-center">
            <div className="container mx-auto px-4">
              <a
                href={`/${language}#services`}
                className="inline-flex items-center text-accent hover:text-accent/80 mb-4 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" size={16} />
                {t("servicePage.backToServices")}
              </a>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-lg bg-accent flex items-center justify-center">
                  <Bus className="w-8 h-8 text-primary" size={32} />
                </div>
                <h1 className="text-3xl md:text-5xl font-bold text-primary-foreground">
                  {t("bus.pageTitle")}
                </h1>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-secondary">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">
                  {t("bus.h2")}
                </h2>
                <p className="text-muted-foreground mb-6">{t("bus.p1")}</p>

                <h3 className="text-xl font-semibold text-foreground mb-4">
                  {t("bus.whatIsEcuTitle")}
                </h3>
                <p className="text-muted-foreground mb-6 whitespace-pre-line">
                  {t("bus.whatIsEcuText")}
                </p>

                <h3 className="text-xl font-semibold text-foreground mb-4">
                  {t("bus.ecuResponsibilitiesTitle")}
                </h3>
                <ul className="space-y-3 mb-6">
                  {(["bus.ecuResp1","bus.ecuResp2","bus.ecuResp3","bus.ecuResp4","bus.ecuResp5"] as const).map((key) => (
                    <li key={key} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" size={20} />
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground mb-8">
                  {t("bus.ecuResp6")}
                </p>

                <h3 className="text-xl font-semibold text-foreground mb-4">
                  {t("bus.symptomsTitle")}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {t("bus.symptomsIntro")}
                </p>
                <ul className="space-y-3 mb-4">
                  {(["bus.symptom1","bus.symptom2","bus.symptom3","bus.symptom4","bus.symptom5","bus.symptom6"] as const).map((key) => (
                    <li key={key} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" size={20} />
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground">
                  {t("bus.symptomsWarning")}
                </p>
              </div>

              <div>
                <img
                  src={serviceBus}
                  alt={t("bus.pageTitle")}
                  className="w-full max-w-md h-auto rounded-lg mb-8 shadow-lg mx-auto lg:mx-0"
                />

                <h3 className="text-xl font-semibold text-foreground mb-4">
                  {t("servicePage.scopeOfServices")}
                </h3>
                <ul className="space-y-3 mb-8">
                  {(["bus.scope1","bus.scope2","bus.scope3","bus.scope4","bus.scope5","bus.scope6","bus.scope7"] as const).map((key) => (
                    <li key={key} className="flex items-center gap-3 text-foreground">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" size={20} />
                      {t(key)}
                    </li>
                  ))}
                </ul>

                <h3 className="text-xl font-semibold text-foreground mb-4">
                  {t("bus.typesTitle")}
                </h3>
                <ul className="space-y-3 mb-8">
                  {(["bus.type1","bus.type2","bus.type3","bus.type4","bus.type5"] as const).map((key) => (
                    <li key={key} className="flex items-center gap-3 text-foreground">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" size={20} />
                      {t(key)}
                    </li>
                  ))}
                </ul>

                <h3 className="text-xl font-semibold text-foreground mb-4">
                  {t("bus.coverageTitle")}
                </h3>
                <p className="text-muted-foreground">
                  {t("bus.coverageText")}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <ContactInfo />
              <ContactForm />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default BusControllers;
